import type { Item, LinkedItem } from '@types'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { toVector3 } from '@utils/conversion/toVector3'
import { Box3, Quaternion, Vector3 } from 'three'

function isDrawFitUnit(item: Item | LinkedItem): boolean {
  return (
    (isItem(item) || isLinkedItem(item)) &&
    Boolean(item.matrix) &&
    Boolean(item.width) &&
    Boolean(item.height)
  )
}

export type DrawFitFootprint = {
  center: Vector3
  footprintWidth: number
  footprintDepth: number
  maxY: number
  anchorQuaternion: Quaternion
}

/**
 * Huella real de los módulos en el espacio local del primer item (respeta rotación).
 * Usado por el encuadre cenital y coherente con `ItemDimensions3D`.
 */
export function computeItemsFootprintForDrawFit(
  items: Map<string, Item | LinkedItem>,
): DrawFitFootprint | null {
  const regularItems = Array.from(items.values()).filter(isDrawFitUnit)
  if (regularItems.length === 0) return null

  const anchorItem = regularItems[0]
  if (!anchorItem?.matrix) return null

  const anchorPosition = new Vector3()
  const anchorQuaternion = new Quaternion()
  anchorItem.matrix.decompose(anchorPosition, anchorQuaternion, new Vector3())
  const inverseAnchorQuaternion = anchorQuaternion.clone().invert()

  let minX = Infinity
  let minY = Infinity
  let minZ = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let maxZ = -Infinity
  const worldBounds = new Box3()

  for (const item of regularItems) {
    if (!item.matrix || !item.width || !item.height) continue

    const position = new Vector3()
    const quaternion = new Quaternion()
    item.matrix.decompose(position, quaternion, new Vector3())

    const itemWidth = item.width || 0
    const itemThickness = item.thickness || 0
    const itemHeight = item.height || 0
    const corners = [
      new Vector3(0, 0, 0),
      new Vector3(itemWidth, 0, 0),
      new Vector3(0, itemHeight, 0),
      new Vector3(itemWidth, itemHeight, 0),
      new Vector3(0, 0, itemThickness),
      new Vector3(itemWidth, 0, itemThickness),
      new Vector3(0, itemHeight, itemThickness),
      new Vector3(itemWidth, itemHeight, itemThickness),
    ]

    for (const corner of corners) {
      const worldCorner = corner.clone().applyQuaternion(quaternion).add(position)
      worldBounds.expandByPoint(worldCorner)
      const localCorner = worldCorner
        .clone()
        .sub(anchorPosition)
        .applyQuaternion(inverseAnchorQuaternion)

      if (localCorner.x < minX) minX = localCorner.x
      if (localCorner.y < minY) minY = localCorner.y
      if (localCorner.z < minZ) minZ = localCorner.z
      if (localCorner.x > maxX) maxX = localCorner.x
      if (localCorner.y > maxY) maxY = localCorner.y
      if (localCorner.z > maxZ) maxZ = localCorner.z
    }
  }

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minZ) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxZ)
  ) {
    return null
  }

  const localCenter = new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2)
  const center = localCenter.applyQuaternion(anchorQuaternion).add(anchorPosition)

  return {
    center,
    footprintWidth: maxX - minX,
    footprintDepth: maxZ - minZ,
    maxY: worldBounds.max.y,
    anchorQuaternion,
  }
}

/**
 * Calcula el bounding box de todas las unidades (items) en la escena
 * y retorna el centro y el tamaño para ajustar la cámara
 */
export function calculateItemsBoundingBox(items: Map<string, Item | LinkedItem>): {
  center: Vector3
  size: Vector3
  min: Vector3
  max: Vector3
} | null {
  const regularItems = Array.from(items.values()).filter(
    (item) => (isItem(item) || isLinkedItem(item)) && item.matrix && item.width && item.height,
  )

  if (regularItems.length === 0) {
    return null
  }

  const boundingBox = new Box3()
  let hasValidItem = false

  // Calcular el bounding box de todos los items
  for (const item of regularItems) {
    if (!item.matrix || !item.width || !item.height) continue

    // Obtener la posición del item desde la matriz
    const position = new Vector3()
    const quaternion = new Quaternion()
    const scale = new Vector3()
    item.matrix.decompose(position, quaternion, scale)

    // Calcular las dimensiones del item
    const halfWidth = (item.width || 0) / 2
    const halfHeight = (item.height || 0) / 2
    const halfThickness = (item.thickness || 0) / 2

    // Crear un bounding box local para este item
    const itemMin = toVector3([position.x - halfWidth, position.y, position.z - halfThickness])
    const itemMax = toVector3([
      position.x + halfWidth,
      position.y + halfHeight,
      position.z + halfThickness,
    ])

    // Expandir el bounding box global
    if (!hasValidItem) {
      boundingBox.setFromPoints([itemMin, itemMax])
      hasValidItem = true
    } else {
      boundingBox.expandByPoint(itemMin)
      boundingBox.expandByPoint(itemMax)
    }
  }

  if (!hasValidItem) {
    return null
  }

  const center = new Vector3()
  const size = new Vector3()
  boundingBox.getCenter(center)
  boundingBox.getSize(size)
  const min = boundingBox.min.clone()
  const max = boundingBox.max.clone()

  return { center, size, min, max }
}

/**
 * Calcula la distancia de la cámara necesaria para ver completamente el bounding box
 */
export function calculateCameraDistance(
  size: Vector3,
  fov: number = 45,
  aspect: number = 1,
  padding: number = 1.5,
): number {
  // Calcular la distancia necesaria basándose en el tamaño más grande
  const maxDimension = Math.max(size.x, size.y, size.z)

  // Fórmula para calcular la distancia basada en el FOV
  // distance = (size / 2) / tan(fov / 2)
  const halfFov = (fov * Math.PI) / 180 / 2
  const distance = maxDimension / 2 / Math.tan(halfFov)

  // Aplicar padding para asegurar que todo esté visible
  return distance * padding
}
